<template>
	<BaseLayout :footer-full="true" :include-padding="false" :background-color-class="`bg-color-white`">
		<BasePlaceHolder :line-count="4" :section-count="4" class="container pt-300 pb-300" :placehold="loading">
			<article v-if="article">
				<div class="grid">
					<header class="col-12 col-10-md col-offset-1-md col-8-lg col-offset-2-lg">
						<BaseAlert v-if="articleAlert" class="mb-300" :alert="articleAlert" data-test="article-alert" />
						<p class="overline">
							<BaseLink :link="blogLanding" classes="cta-link--rust article-category" />
							<BaseLink
								v-if="category"
								:link="category"
								classes="cta-link--rust article-category category-name"
							/>
						</p>
						<h1 class="heading-1 mb-100 mt-100">{{ article.title }}</h1>
						<p v-if="article?.subtitle" class="body-xl">
							{{ article?.subtitle }}
						</p>
						<p class="blog-byline">
							<span
								data-test="article-author"
								:class="[
									'display-inline-block',
									{
										'text-transform-capitalize': article?.author?.name
									}
								]"
							>
								{{ author }}
							</span>
							<ArticleDate
								v-if="article?.date"
								class="bl ml-50 pl-50"
								:date-string="article?.date || article._createdAt"
								:format="CustomSpacetimeFormat.MONTH_DATE_PAD_YEAR"
							/>
						</p>
					</header>

					<figure
						v-if="!article.hideCoverImage && article.coverImage && article.coverImage.asset.url"
						class="ml-0 mt-150 mr-0 mb-0 col-12 col-offset-1-lg col-10-lg"
						role="presentation"
						data-test="article-cover-image-container"
					>
						<ArticleCoverImage :cover-image="article.coverImage.asset" />
					</figure>
				</div>

				<section class="display-flex display-block-lg grid-lg flex-wrap-wrap justify-content-center">
					<div class="col-12 col-10-md col-8-lg col-offset-2-lg article-body pt-200">
						<p v-if="article?.lead" class="article-lead mb-100 body-xl">
							{{ article.lead }}
						</p>

						<ArticleBodyContent
							v-if="bodyContent"
							:body-content="bodyContent"
							:key-takeaways="keyTakeaways"
						/>
						<aside v-if="showCta" class="article-cta pl-100 pt-150 pr-100 pb-150 mt-150 mb-150 grid">
							<div class="col-12 col-8-md col-offset-2-md">
								<p class="article-cta-heading heading-6 mb-100">
									{{ article?.emailCapturePrompt ?? 'We make investing simple. Discover how.' }}
								</p>
								<LeadCaptureComponent class="mt-100" :form-config="leadCaptureFormConfig" />
							</div>
						</aside>
						<aside v-else-if="displayMicroFeedback" class="my-300">
							<div class="col-6-md">
								<TransitionCollapse>
									<div data-test="radio-feedback-wrapper">
										<span class="body-md font-weight-bold"
											>How would you rate your overall satisfaction with this experience?</span
										>
										<MicroFeedbackRadios @reaction-selected="reactionSelected" />
									</div>
								</TransitionCollapse>
							</div>
						</aside>
						<footer
							v-if="footnotes.length"
							class="article-disclaimers disclaimer mt-100 pt-100 pt-200-md mt-200-md"
						>
							<RichTextComponent :blocks="footnotes" />
						</footer>
					</div>
				</section>
			</article>
		</BasePlaceHolder>

		<section
			v-if="recommended.length > 0"
			class="article-list-recommended bg-color-greige pt-300 pb-300"
			:class="'recommended-count-' + recommended.length"
			data-test="recommended-articles-section"
		>
			<BasePlaceHolder :line-count="4" :placehold="loading" class="container">
				<h2 class="heading-5 mb-50">Recommended articles</h2>
				<div v-if="recommended.length > 0" class="display-flex flex-wrap-wrap gutters-300-md">
					<ArticleCard
						v-for="(article, articleId) in recommended"
						:key="articleId"
						:article="article"
						:base-link-classes="'article-list-item-inner'"
						:classes="recommendedColClass + ' col-12 col-6-md mt-100 mb-50'"
					/>
				</div>
			</BasePlaceHolder>
		</section>

		<template #footer>
			<BasePlaceHolder
				v-if="article?.flatFooterDisclaimers"
				:line-count="3"
				:placehold="loading"
				:class="{ container: loading }"
			>
				<FooterFull :disclaimers="article.flatFooterDisclaimers" />
			</BasePlaceHolder>
		</template>
	</BaseLayout>
</template>

<script setup lang="ts">
import { BaseAlertComponentType, Link } from 'types/layout';
import { computed, onUnmounted, ref } from 'vue';
import { SanityArticle, SanityArticleTeaser, SanityKeyTakeaway } from 'types/sanity/blog-education-center';
import { useRoute, useRouter } from 'vue-router';
import { appCloudfrontPath } from '@constants';
import ArticleBodyContent from '@components/blog/article-body-content.vue';
import ArticleCard from '@components/blog/article-card.vue';
import ArticleCoverImage from '@components/blog/article-cover-image.vue';
import ArticleDate from '@components/blog/article-date.vue';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import FooterFull from '@components/footer/footer-full.vue';
import { getSanityBlogArticle } from '@api/sanity/graphql/blog-education-center';
import { hasSubmittedMicrofeedback } from '@api/micro-feedback';
import LeadCaptureComponent from '@components/lead-capture/lead-capture-form.vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import MicroFeedbackRadioFormModal from '@components/micro-feedback/micro-feedback-radio-form-modal.vue';
import MicroFeedbackRadios from '@components/micro-feedback/micro-feedback-radios.vue';
import { MicroFeedbackReactionType } from 'types/micro-feedback';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityRichTextBlock } from 'types/sanity';
import { trackContent } from '@api/content-track';
import TransitionCollapse from '@components/layout/transition-collapse.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useMixpanel } from '@utils/composables/use-mixpanel';

const route = useRoute();
const router = useRouter();

const appStore = useAppStore();

const headControl = useHead({
	script: [`window.prerenderReady = false;`]
});

const blogLanding: Link = {
	text: 'Education',
	router: 'blog-landing'
};

const leadCaptureFormConfig: LeadCaptureFormConfig = {
	centerButton: true,
	placeholder: 'Enter your email',
	signupFlowType: 'STANDARD'
};

const loading = ref(true);

const article = ref<SanityArticle | null>(null);

const author = computed(() => {
	return `By ${article.value?.author?.name ?? 'the Fundrise Team'}`;
});

const category = computed<Link | null>(() => {
	if (article.value?.category?.name && article.value?.category?.slug) {
		return {
			text: article.value.category.name,
			router: 'blog-category',
			params: { categoryUrlSlug: article.value.category.slug.current }
		};
	}
	return null;
});

const articleAlert = computed((): BaseAlertComponentType | null => {
	if (!article.value?.customArticleAlert?.alertMessage || !article.value?.customArticleAlert?.showAlert) return null;

	const ctaLink: Link | undefined =
		article.value?.customArticleAlert?.alertCTA?.url && article.value?.customArticleAlert?.alertCTA?.linkText
			? {
					text: article.value.customArticleAlert.alertCTA.linkText,
					href: article.value.customArticleAlert.alertCTA.url,
					target: article.value.customArticleAlert.alertCTA.openInNewTab ? '_blank' : ''
				}
			: undefined;

	const alertMessage: Array<string> = article.value?.customArticleAlert?.alertMessage.split('\n') ?? [];

	return {
		type: article.value?.customArticleAlert?.alertType ?? 'info',
		heading: article.value?.customArticleAlert?.alertHeading ?? undefined,
		message: alertMessage,
		cta: ctaLink
	};
});

const bodyContent = computed((): Array<SanityRichTextBlock> => {
	return article.value?.body ?? [];
});

const footnotes = computed((): Array<SanityRichTextBlock> => {
	return article.value?.footnotes ?? [];
});

const keyTakeaways = computed((): Array<SanityKeyTakeaway> => {
	return article.value?.keyTakeawaysSection ?? [];
});

const recommended = ref<Array<SanityArticleTeaser>>([]);
const reaction = ref<MicroFeedbackReactionType | null>(null);

const urlSlug = ref('');

const displayMicroFeedback = ref(false);

const showCta = computed((): boolean => {
	return !!article.value?.addEmailCaptureCta && !appStore.isAuthenticated;
});

const recommendedColClass = computed((): string => {
	if (recommended.value.length < 3) {
		return 'col-6-lg';
	} else if (recommended.value.length === 3) {
		return 'col-4-lg';
	}
	return 'col-3-lg';
});

const { trackMixpanelEvent } = useMixpanel();

function reactionSelected(selectedReaction: MicroFeedbackReactionType): void {
	const mixpanelData = {
		'Entry Point': 'Education Article',
		'Event Type': 'EDUCATION_ARTICLE',
		'Associated Event ID': urlSlug.value,
		'Satisfaction Response': selectedReaction
	};
	trackMixpanelEvent('Rating clicked', mixpanelData);
	reaction.value = selectedReaction;
	openMicroFeedbackModal();
}

function openMicroFeedbackModal(): void {
	appStore.updateCurrentModal({
		modal: MicroFeedbackRadioFormModal,
		props: {
			microFeedbackEventType: 'EDUCATION_ARTICLE',
			associatedEventId: urlSlug.value,
			preSelectedResponse: reaction.value
		}
	});
	displayMicroFeedback.value = false;
}

async function setup(): Promise<void> {
	urlSlug.value = route.params.blogPostUrlSlug as string;

	try {
		const sanityArticle = await getSanityBlogArticle(route);
		article.value = sanityArticle;
		recommended.value = sanityArticle?.recommended ?? [];
	} catch {
		await router.push({ path: '/404' });
	}

	if (appStore.isAuthenticated && article.value && article.value.display_microfeedback) {
		trackContent(urlSlug.value, 'ARTICLE');
		if (article.value.display_microfeedback) {
			const submittedMicrofeedback = await hasSubmittedMicrofeedback('EDUCATION_ARTICLE', urlSlug.value);
			displayMicroFeedback.value = !submittedMicrofeedback;
		}
	}

	if (article.value) {
		headControl?.patch({
			title: article.value.title,
			meta: [
				{ property: 'og:title', content: article.value.title },
				{ name: 'description', content: article.value?.abstract ?? '' },
				{ property: 'og:description', content: article.value?.abstract ?? '' },
				{
					property: 'og:image',
					content:
						article.value.coverImage && article.value.coverImage.asset.url
							? article.value.coverImage.asset.url
							: `${appCloudfrontPath}/images/global/Fundrise-og-image.jpg`
				}
			],
			script: [`window.prerenderReady = true;`]
		});
	} else {
		await router.push({
			path: '/404'
		});
	}
	loading.value = false;
}

setup();

onUnmounted(() => {
	headControl?.dispose();
});
</script>
